import React, { useCallback, useEffect, useState, useRef, Fragment, lazy } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle2, TrendingUp, Monitor, Calendar, Headset, BarChart3, Target, Users, ShieldCheck, Zap, LayoutTemplate, Palette, MessageSquareQuote, ExternalLink, Shield, Maximize2, Minimize2, Presentation } from 'lucide-react';
// Brand Colors
const colors = {
  teal: '#2EAD6D',
  amber: '#F5A623',
  dark: '#0F0F0F',
  darkCard: '#1A1A1A'
};
const LOGO_LEVATE = 'https://cdn.magicpatterns.com/uploads/txUGJVNRA288c6kQWwcnmn/logo.png';
const LOGO_VIRIDIA = 'https://cdn.magicpatterns.com/uploads/wR1kBZZTaK1wFgutmfc7P9/pasted-image.png';
export function PitchDeck() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const totalSlides = 16;
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => Math.min(prev + 1, totalSlides - 1));
  }, [totalSlides]);
  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  // Speaker Notes
  const slideNotes: {
    title: string;
    notes: string;
  }[] = [{
    title: 'Title Slide',
    notes: `Welcome everyone — thanks for being here. My name is [Your Name] with Viridia Analytics, and today we're walking through exactly what your partnership with L-Evate Network and Viridia looks like from day one.\n\nThis isn't a sales pitch — you've already made the investment. This is about showing you the system you now have access to, the team behind it, and how we're going to work together to get patients through your door.\n\nLet's jump in.`
  }, {
    title: 'Agenda',
    notes: `Here's a quick look at what we'll cover today. We're going to talk about what you actually get as a partner, how we handle your brand and website, the support structure, how we continuously optimize your campaigns, and what we need from you to make this work.\n\nThis should take about 20–25 minutes, and we'll leave time for questions at the end. Sound good? Let's go.`
  }, {
    title: 'Strategic Partnership Overview',
    notes: `So let's start with the big picture — what's actually included in your partnership.\n\nYou're getting full Meta ad management — that's Facebook and Instagram — optimized specifically for lead generation. We handle the creative, the targeting, the optimization. You also get up to 3 custom ad creatives per month, a 3-page high-converting website that we host and manage, an integrated telehealth booking system, a dedicated support ticket system with 24-hour typical turnaround, and monthly one-on-one strategy calls.\n\nThis isn't piecemeal — it's a full stack. Everything works together as one system.`
  }, {
    title: 'Patient Acquisition System',
    notes: `I want to reframe something here — because this is important. You didn't just buy a license. You invested in a patient acquisition system.\n\nWhat does that mean? It means you now have access to proven ad systems that have been tested across healthcare and wellness markets. You have conversion-focused lead handling — from the first click all the way to a booked appointment. You have a dedicated account manager who's in the trenches with you. And you have a team that actually understands healthcare marketing — compliance, patient psychology, what drives real bookings.\n\n[Point to the pipeline at the bottom] This is the flow: Attention turns into a lead, the lead gets followed up with, that becomes a booking, and that booking becomes patient growth. That's the engine. That's what you now own.`
  }, {
    title: 'The Team Behind Your Growth',
    notes: `Now let me tell you about the team that's going to be executing this for you.\n\nViridia Analytics has worked with hundreds of practices — doctor's offices, med spas, dentists, chiropractors, wellness businesses. We're not a generic agency. We specialize in healthcare and wellness acquisition.\n\n[Walk through the pillars] We lead with conversion-first strategy — not vanity metrics. We have speed-to-lead systems so your leads don't go cold. We provide hands-on reporting and optimization — not set-it-and-forget-it. And everything we do is focused on one thing: getting patients booked.\n\nYou now have a trusted team and a clear path forward. So let's show you exactly how the system works.`
  }, {
    title: 'How Billing Works',
    notes: `Let's talk about how the billing side works so there are no surprises.\n\nFirst — ad spend. Your payment method goes on YOUR Meta Ads account. You own it. You grant us partner access so we can build, manage, and optimize your campaigns. We set this up during onboarding.\n\nSecond — new Meta accounts sometimes have a billing threshold that limits how much you can spend early on. That's a Meta thing, not us. But we CAN control your campaign budget, creatives, copy, and goals — so we work within whatever threshold you have.\n\nThird — invoicing. We invoice separately for the monthly retainer and the 10% management fee, which is based on your previous month's ad spend. Simple and transparent.`
  }, {
    title: 'Onboarding Roadmap',
    notes: `Here's what the first 30 days look like — and this moves fast.\n\nWeek 1 is all about branding and intake. We do a discovery call, nail down your logo, select your website template, secure your domain, and get integrations started.\n\nWeek 2, we're building. Your website goes up, SEO gets configured, we start on your ad creatives, and we review everything together.\n\nWeek 3 is review and hosting. Final approvals, hosting setup, last round of approvals on creatives.\n\nWeek 4 — you're live. Site is up, campaigns launch, and we start driving traffic. The only caveat is if you're pending Legit Script certification, which some verticals require.\n\nThe point is: within 30 days, you have a fully operational acquisition system running.`
  }, {
    title: 'Proven Acquisition System',
    notes: `I want to take a second to reinforce why this system works — because I know you're making a big investment and you want to feel confident about it.\n\nFirst, everything is data-driven. We don't guess. We test, measure, and refine based on real performance data.\n\nSecond, this is scalable and repeatable. Once we dial in your campaigns, scaling is predictable — more spend, more patients, same efficiency.\n\nThird, you have full-stack infrastructure. Ads, website, booking, tracking, reporting — all under one roof. No gaps, no finger-pointing.\n\nAnd fourth, this is a real partnership. Monthly strategy calls, fast support turnaround, and a team that's invested in your growth.\n\nWe don't just run ads — we build acquisition engines.`
  }, {
    title: 'Performance Reviews',
    notes: `We don't just launch and disappear. Here's how we stay on top of your performance.\n\nAt 30 days, we do a check-in call. We review early campaign data, look at lead quality, and adjust targeting and spend as needed. This is about making sure we're pointed in the right direction.\n\nAt 60 days, we do a mid-point review. We're analyzing conversion rates, cost-per-lead, and optimizing anything that's underperforming.\n\nAt 90 days, we do a full performance audit. We compare results against your original KPIs, present an ROI report, and map out the next quarter's roadmap.\n\nThis cadence keeps us accountable and keeps your campaigns improving.`
  }, {
    title: 'Success Requirements',
    notes: `Now here's the part where I need to be real with you — because this is a partnership, and partnerships go both ways.\n\nWe need account access right away — ad accounts, domain, brand assets. Delays here delay everything.\n\nYour ad spend needs to be funded directly on the platforms. We manage it, you fund it.\n\nFeedback loop is critical. When we send you creatives or strategy for approval, we need a response within 48 hours. Slow approvals slow down your launch.\n\nYou're responsible for the sales side — consultations, closing, onboarding patients. We bring the leads, you convert them.\n\nCompliance is on you — make sure your offers and claims meet medical and legal regulations.\n\nAnd finally, share feedback on lead quality. That data is gold for us to optimize your campaigns.\n\nWhen both sides show up, this thing works.`
  }, {
    title: 'Website Hosting & Templates',
    notes: `Let's talk about your website. We host through GoDaddy, and we'll secure your .com domain, build your site, and develop your brand identity.\n\nYou get to choose from three template options — Performance Focus, Clinical/Evidence Focus, or Lifestyle/Transformation Focus. Each one is designed to convert visitors into booked appointments.\n\n[Point to the previews] You can click these to see full previews of each template. We'll customize whichever one you choose with your brand colors, logo, and imagery.\n\nA couple of notes: all sites include a "Powered by Viridia Analytics" footer. Additional pages, widgets, or funnels beyond the 3-page build are available as add-ons. And if you already have a domain and website, we'll just need access to set up tracking — Meta Pixel, UTMs, that sort of thing.`
  }, {
    title: 'Build a Trusted Brand',
    notes: `Branding matters — especially in healthcare. Patients choose providers they trust, and trust starts with how you present yourself.\n\nWe'll help you develop three core brand elements: your logo, your color palette, and your brand motto. Here's an example of what that looks like.\n\nNow — if you already have branding, that's perfect. We'll take what you have and plug it into high-performing ad creatives that actually get clicks. We're not here to reinvent your brand — we're here to make it work harder for you.`
  }, {
    title: 'A La Carte Add-Ons',
    notes: `Beyond your core partnership, we offer a menu of add-on services you can layer in as your business grows.\n\n[Walk through the table briefly] Things like a dedicated GHL phone number, email marketing campaigns, social media automation, SMS broadcasting, advanced pipeline customization, and an AI voice agent.\n\nAnd here's the deal — anything you add within the first 14 days of launch gets a 10% discount. So if you know you want any of these, now's the time.\n\nThese are all optional — your core system is fully operational without them. But they're here when you're ready to scale.`
  }, {
    title: 'Legal Disclaimer',
    notes: `I'll keep this quick — this is the standard legal stuff. Everything in this presentation is for informational purposes. Results vary based on your market, effort, and business decisions. We don't guarantee specific revenue or outcomes.\n\nYou're operating as an independent contractor, not an employee or agent of Viridia Analytics. And as always, consult your own legal and tax advisors for anything specific to your situation.\n\nNothing surprising here — just want to make sure we're transparent about everything.`
  }, {
    title: 'Contact Info',
    notes: `Here's how to reach us. Our phone line is (623) 300-0708, and we're available Monday through Friday, 9 AM to 4 PM Arizona time.\n\nFor general inquiries, email info@viridiaanalytics.com. For direct support, your point of contact is Kevin at kevin@viridiaanalytics.com.\n\nYou'll also have access to our support ticket system once you're onboarded, which has a typical 24-hour turnaround.`
  }, {
    title: 'Questions',
    notes: `And that's the full picture. You now know exactly what you have access to, how the system works, who's behind it, and what the first 30 days look like.\n\nThe floor is yours — what questions do you have?\n\n[Let the conversation flow naturally. Address concerns directly and confidently. If they ask about results, reference the review cadence and data-driven approach without making specific promises. If they ask about timeline, reference the onboarding roadmap.]`
  }];
  // Presenter Window
  const presenterWindowRef = useRef<Window | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    channelRef.current = new BroadcastChannel('pitchdeck-sync');
    channelRef.current.onmessage = e => {
      if (e.data.type === 'slide-change') {
        setCurrentSlide(e.data.slide);
      }
    };
    return () => channelRef.current?.close();
  }, []);
  useEffect(() => {
    channelRef.current?.postMessage({
      type: 'slide-update',
      slide: currentSlide
    });
    if (presenterWindowRef.current && !presenterWindowRef.current.closed) {
      updatePresenterWindow(currentSlide);
    }
  }, [currentSlide]);
  const updatePresenterWindow = (slide: number) => {
    const win = presenterWindowRef.current;
    if (!win || win.closed) return;
    const notesEl = win.document.getElementById('speaker-notes');
    const slideNum = win.document.getElementById('slide-num');
    const slideTitle = win.document.getElementById('slide-title');
    const nextTitle = win.document.getElementById('next-title');
    if (notesEl) notesEl.innerText = slideNotes[slide]?.notes || '';
    if (slideNum) slideNum.innerText = `${slide + 1} / ${totalSlides}`;
    if (slideTitle) slideTitle.innerText = slideNotes[slide]?.title || '';
    if (nextTitle) nextTitle.innerText = slide < totalSlides - 1 ? `Next: ${slideNotes[slide + 1]?.title}` : 'End of presentation';
  };
  const openPresenterView = () => {
    const win = window.open('', 'PresenterView', 'width=700,height=800,left=100,top=100');
    if (!win) return;
    presenterWindowRef.current = win;
    const css = ['* { margin: 0; padding: 0; box-sizing: border-box; }', 'body { background: #0F0F0F; color: #fff; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; padding: 32px; height: 100vh; display: flex; flex-direction: column; }', '.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid rgba(255,255,255,0.1); }', '.slide-num { font-size: 14px; color: #8C8C8C; font-weight: 600; }', '.slide-title { font-size: 24px; font-weight: 700; color: #2EAD6D; }', '.notes-container { flex: 1; overflow-y: auto; margin-bottom: 24px; }', '.notes { font-size: 18px; line-height: 1.8; color: #E0E0E0; white-space: pre-wrap; }', '.footer { padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.1); display: flex; justify-content: space-between; align-items: center; }', '.next-title { font-size: 14px; color: #F5A623; font-weight: 500; }', '.timer { font-size: 14px; color: #8C8C8C; font-variant-numeric: tabular-nums; }', '.nav-btns { display: flex; gap: 8px; margin-top: 16px; }', '.nav-btn { background: #1A1A1A; border: 1px solid rgba(255,255,255,0.1); color: #fff; padding: 8px 20px; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500; }', '.nav-btn:hover { background: #2a2a2a; }', '.notes-container::-webkit-scrollbar { width: 6px; }', '.notes-container::-webkit-scrollbar-track { background: transparent; }', '.notes-container::-webkit-scrollbar-thumb { background: #333; border-radius: 3px; }'].join('\n');
    const maxSlide = totalSlides - 1;
    const curTitle = slideNotes[currentSlide]?.title || '';
    const curNotes = (slideNotes[currentSlide]?.notes || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const curNum = currentSlide + 1 + ' / ' + totalSlides;
    const nextLabel = currentSlide < totalSlides - 1 ? 'Next: ' + (slideNotes[currentSlide + 1]?.title || '') : 'End of presentation';
    const scriptContent = ['var seconds = 0;', 'setInterval(function() {', '  seconds++;', '  var m = String(Math.floor(seconds / 60)).padStart(2, "0");', '  var s = String(seconds % 60).padStart(2, "0");', '  document.getElementById("timer").innerText = m + ":" + s;', '}, 1000);', 'var ch = new BroadcastChannel("pitchdeck-sync");', 'ch.onmessage = function(e) {};', 'function getCurrentSlide() {', '  var text = document.getElementById("slide-num").innerText;', '  return parseInt(text.split(" / ")[0]) - 1;', '}', 'document.getElementById("prev-btn").onclick = function() {', '  ch.postMessage({ type: "slide-change", slide: Math.max(0, getCurrentSlide() - 1) });', '};', 'document.getElementById("next-btn").onclick = function() {', '  ch.postMessage({ type: "slide-change", slide: Math.min(' + maxSlide + ', getCurrentSlide() + 1) });', '};', 'document.addEventListener("keydown", function(e) {', '  if (e.key === "ArrowRight") document.getElementById("next-btn").click();', '  if (e.key === "ArrowLeft") document.getElementById("prev-btn").click();', '});'].join('\n');
    const html = '<!DOCTYPE html><html><head><title>Presenter View</title><style>{`' + css + '`}</style></head><body>' + '<div class="header">' + '<div class="slide-title" id="slide-title">' + curTitle + '</div>' + '<div class="slide-num" id="slide-num">' + curNum + '</div>' + '</div>' + '<div class="notes-container"><div class="notes" id="speaker-notes">' + (slideNotes[currentSlide]?.notes || '') + '</div></div>' + '<div class="footer">' + '<div class="next-title" id="next-title">' + nextLabel + '</div>' + '<div class="timer" id="timer">00:00</div>' + '</div>' + '<div class="nav-btns">' + '<button class="nav-btn" id="prev-btn">&#8592; Previous</button>' + '<button class="nav-btn" id="next-btn">Next &#8594;</button>' + '</div>' + '<script>' + scriptContent + '<\/script>' + '</body></html>';
    win.document.write(html);
    win.document.close();
  };
  // Slide Renderers
  const slides = [
  // SLIDE 1: TITLE
  () => <div className="flex flex-col items-center justify-center h-full text-center px-4" data-id="element-1">
        <div className="flex items-center justify-center gap-8 mb-12" data-id="element-2">
          <div className="bg-white/5 p-6 rounded-2xl backdrop-blur-sm border border-white/10" data-id="element-3">
            <img src={LOGO_LEVATE} alt="L-Evate" className="h-16 md:h-20 object-contain" data-id="element-4" />
          </div>
          <span className="text-3xl font-light text-gray-500" data-id="element-5">x</span>
          <div className="rounded-2xl" data-id="element-6">
            <img src={LOGO_VIRIDIA} alt="Viridia Analytics" className="h-32 md:h-40 object-contain" data-id="element-7" />
          </div>
        </div>
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight" data-id="element-8">
          L-EVATE NETWORK <span className="text-[#2EAD6D]" data-id="element-9">x</span> VIRIDIA
          ANALYTICS
        </h1>
        <h2 className="text-2xl md:text-3xl text-[#F5A623] font-medium mb-4" data-id="element-10">
          Licensee Partnership & Onboarding Program
        </h2>
        <p className="text-xl text-gray-400 max-w-2xl" data-id="element-11">
          Your Dedicated Patient Acquisition Partner
        </p>
        <div className="mt-16 w-24 h-1 bg-gradient-to-r from-[#F5A623] to-[#2EAD6D] rounded-full" data-id="element-12"></div>
      </div>,
  // SLIDE 2: AGENDA
  () => <div className="flex flex-col justify-center h-full max-w-4xl mx-auto px-6" data-id="element-13">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-14">
          Agenda
        </h2>
        <p className="text-xl text-[#2EAD6D] mb-12" data-id="element-15">
          We'll cover what this partnership means for you
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-16">
          {['What you get as a partner', 'Brand | Templates | Customization', 'Support', 'Ongoing Performance Optimization', 'Your responsibilities'].map((item, idx) => <div key={idx} className="flex items-center gap-4 bg-[#1A1A1A] p-6 rounded-xl border border-white/5 hover:border-[#F5A623]/30 transition-colors" data-id="element-17">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#2EAD6D]/10 text-[#2EAD6D] font-bold text-lg border border-[#2EAD6D]/20" data-id="element-18">
                {idx + 1}
              </div>
              <span className="text-lg text-gray-200 font-medium" data-id="element-19">{item}</span>
            </div>)}
        </div>
      </div>,
  // SLIDE 3: OVERVIEW
  () => <div className="flex flex-col justify-center h-full max-w-6xl mx-auto px-6" data-id="element-20">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-21">
          Strategic Partnership Overview
        </h2>
        <p className="text-xl text-[#F5A623] mb-12" data-id="element-22">
          What You Get as a Licensee Partner
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-23">
          {[{
        icon: Target,
        text: 'Core Meta (FB/IG) Ad Management - Optimized for Lead Gen'
      }, {
        icon: Palette,
        text: 'Custom Ad Creative Development (Up to 3 per month)'
      }, {
        icon: LayoutTemplate,
        text: '3-Page High-Converting Website (Hosted & Managed)'
      }, {
        icon: Calendar,
        text: 'Integrated Telehealth Booking System'
      }, {
        icon: Headset,
        text: 'Dedicated Support Ticket System (24-hr typical turnaround)'
      }, {
        icon: TrendingUp,
        text: 'Monthly 1-on-1 Strategy Optimization Calls (30 mins)'
      }].map((item, idx) => <div key={idx} className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 relative overflow-hidden group" data-id="element-24">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#2EAD6D] to-[#F5A623] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-25"></div>
              <item.icon className="w-10 h-10 text-[#2EAD6D] mb-6" data-id="element-26" />
              <p className="text-lg text-gray-200 leading-relaxed" data-id="element-27">
                {item.text}
              </p>
            </div>)}
        </div>
      </div>,
  // SLIDE 4: PATIENT ACQUISITION SYSTEM (NEW)
  () => <div className="flex flex-col justify-center h-full max-w-6xl mx-auto px-6" data-id="element-28">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-29">
          You Didn't Just Buy a License —
          <br data-id="element-30" />
          <span className="text-[#2EAD6D]" data-id="element-31">
            You Invested in a Patient Acquisition System
          </span>
        </h2>
        <p className="text-xl text-gray-400 mb-12 max-w-3xl" data-id="element-32">
          This isn't just marketing — it's a complete patient acquisition engine
          designed to turn attention into booked patients, consistently.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-33">
          {[{
        icon: Target,
        title: 'Proven Ad Acquisition Systems',
        desc: 'Battle-tested campaigns built specifically for healthcare and wellness practices. Not templates — real systems refined across markets.'
      }, {
        icon: Users,
        title: 'Conversion-Focused Lead Handling',
        desc: 'From first click to booked appointment, every touchpoint is designed to convert attention into patients on your schedule.'
      }, {
        icon: Headset,
        title: 'Dedicated Account Management',
        desc: 'Hands-on optimization, monthly strategy calls, and a team that treats your growth like their own.'
      }, {
        icon: ShieldCheck,
        title: 'Healthcare Marketing Expertise',
        desc: 'A team that understands compliance, patient psychology, and what actually drives bookings in medical and wellness verticals.'
      }].map((item, idx) => <div key={idx} className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 flex gap-6 items-start" data-id="element-34">
              <div className="bg-[#2EAD6D]/10 p-4 rounded-xl shrink-0" data-id="element-35">
                <item.icon className="w-8 h-8 text-[#2EAD6D]" data-id="element-36" />
              </div>
              <div data-id="element-37">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-38">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-39">{item.desc}</p>
              </div>
            </div>)}
        </div>

        {/* Visual Flow Pipeline */}
        <div className="bg-[#1A1A1A] rounded-2xl border border-white/5 p-6 overflow-x-auto" data-id="element-40">
          <div className="flex items-center justify-between min-w-[800px]" data-id="element-41">
            {[{
          step: 'Attention',
          color: 'text-gray-400'
        }, {
          step: 'Lead',
          color: 'text-[#F5A623]'
        }, {
          step: 'Follow-Up',
          color: 'text-white'
        }, {
          step: 'Booking',
          color: 'text-[#2EAD6D]'
        }, {
          step: 'Patient Growth',
          color: 'text-[#2EAD6D] font-bold'
        }].map((item, idx, arr) => <Fragment key={idx} data-id="element-42">
                <div className={`text-lg ${item.color} tracking-wide`} data-id="element-43">
                  {item.step}
                </div>
                {idx < arr.length - 1 && <ChevronRight className="w-6 h-6 text-gray-600 mx-4 shrink-0" data-id="element-44" />}
              </Fragment>)}
          </div>
        </div>
      </div>,
  // SLIDE 5: THE TEAM BEHIND YOUR GROWTH (NEW)
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6 py-12" data-id="element-45">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-46">
          The Team Behind Your Growth
        </h2>
        <p className="text-base md:text-lg text-gray-300 mb-8 leading-relaxed border-l-4 border-[#F5A623] pl-5 py-1" data-id="element-47">
          Viridia Analytics has partnered with hundreds of doctor's offices, med
          spas, dentists, chiropractors, and wellness businesses to build and
          manage patient acquisition campaigns.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8" data-id="element-48">
          {['Healthcare & Wellness Acquisition Experience', 'Conversion-First Strategy', 'Speed-to-Lead & Follow-Up Systems', 'Reporting, Optimization & Hands-On Support', 'Focus on Patient Acquisition, Not Vanity Metrics'].map((pillar, idx) => <div key={idx} className="bg-[#1A1A1A] px-5 py-4 rounded-xl border border-white/5 flex items-center gap-3" data-id="element-49">
              <CheckCircle2 className="w-5 h-5 text-[#2EAD6D] shrink-0" data-id="element-50" />
              <span className="text-sm md:text-base text-gray-200 font-medium" data-id="element-51">
                {pillar}
              </span>
            </div>)}
        </div>

        <div className="bg-gradient-to-r from-[#2EAD6D]/20 to-[#F5A623]/20 border border-[#2EAD6D]/30 rounded-2xl p-6 text-center" data-id="element-52">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2" data-id="element-53">
            You now have a trusted team and a clear path forward.
          </h3>
          <p className="text-lg text-[#F5A623] font-medium" data-id="element-54">
            Now let's show you exactly how your system works.
          </p>
        </div>
      </div>,
  // SLIDE 6: BILLING
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6" data-id="element-55">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-10" data-id="element-56">
          How Billing Works
        </h2>

        <div className="space-y-6" data-id="element-57">
          <div className="bg-[#1A1A1A] p-8 rounded-2xl border-l-4 border-[#2EAD6D]" data-id="element-58">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3" data-id="element-59">
              <span className="bg-[#2EAD6D]/20 text-[#2EAD6D] p-2 rounded-lg" data-id="element-60">
                <Target className="w-6 h-6" data-id="element-61" />
              </span>
              Ad Spend
            </h3>
            <p className="text-gray-300 leading-relaxed" data-id="element-62">
              Your payment method will be added on file to your{' '}
              <strong data-id="element-63">OWN Meta Ads Account</strong>. You will grant Viridia
              Analytics Partner permissions to create, manage, and track your
              campaigns. We will accomplish this during initial onboarding.
            </p>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-2xl border-l-4 border-[#F5A623]" data-id="element-64">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3" data-id="element-65">
              <span className="bg-[#F5A623]/20 text-[#F5A623] p-2 rounded-lg" data-id="element-66">
                <ShieldCheck className="w-6 h-6" data-id="element-67" />
              </span>
              Meta Ad Account Threshold
            </h3>
            <p className="text-gray-300 leading-relaxed" data-id="element-68">
              New Meta Ad Accounts may be subject to a limited billing threshold
              as the account builds credibility over time. Until granted access
              from Meta, we cannot control your billing threshold.
              <strong className="text-white block mt-2" data-id="element-69">
                We CAN control Campaign Budget, Creatives, Copies, and Ad Goals.
              </strong>
            </p>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-2xl border-l-4 border-gray-500" data-id="element-70">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3" data-id="element-71">
              <span className="bg-gray-500/20 text-gray-400 p-2 rounded-lg" data-id="element-72">
                <Calendar className="w-6 h-6" data-id="element-73" />
              </span>
              Invoicing
            </h3>
            <p className="text-gray-300 leading-relaxed mb-4" data-id="element-74">
              Viridia Analytics invoices separately for the monthly retainer and
              10% management fee, based on your previous month's ad spend.
            </p>
            <div className="text-sm text-gray-400 bg-black/30 p-4 rounded-lg" data-id="element-75">
              <p className="mb-2" data-id="element-76">
                • The 10% ad management fee will also include advertising on
                other platforms should the licensee opt-in to multi-channel
                advertising.
              </p>
              <p data-id="element-77">
                • Licensee is required to sign a service agreement for other
                products, and ad campaign platforms.
              </p>
            </div>
          </div>
        </div>
      </div>,
  // SLIDE 7: ROADMAP
  () => <div className="flex flex-col justify-center h-full max-w-6xl mx-auto px-6" data-id="element-78">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-79">
          Onboarding Roadmap
        </h2>
        <p className="text-xl text-[#2EAD6D] mb-16" data-id="element-80">
          Phase 1: The First 30 Days
        </p>

        <div className="relative" data-id="element-81">
          {/* Connecting Line */}
          <div className="hidden md:block absolute top-8 left-0 w-full h-1 bg-gradient-to-r from-[#2EAD6D] via-[#F5A623] to-[#2EAD6D] opacity-30" data-id="element-82"></div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-83">
            {[{
          week: 'Week 1',
          title: 'Branding & Intake',
          items: ['Discovery & Brand Intake', 'Logo Design/Selection', 'Template Selection', 'Secure Domain', 'Integrations']
        }, {
          week: 'Week 2',
          title: 'Website & SEO',
          items: ['Refinements', 'Website Build & SEO', 'Change Orders & Peak Review', 'Creatives Started']
        }, {
          week: 'Week 3',
          title: 'Review & Hosting',
          items: ['Final Approved', 'Final Approvals', 'Hosting Setup', 'Approvals']
        }, {
          week: 'Week 4',
          title: 'Go-Live',
          items: ['Site Live!', 'Established', 'Campaign Launch', '(Pending Legit Script Certification)']
        }].map((col, idx) => <div key={idx} className="relative pt-8 md:pt-16" data-id="element-84">
                {/* Timeline Dot */}
                <div className="hidden md:flex absolute top-6 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-[#0F0F0F] border-4 border-[#2EAD6D] z-10" data-id="element-85"></div>

                <div className="bg-[#1A1A1A] rounded-2xl p-6 border border-white/5 h-full hover:border-[#2EAD6D]/50 transition-colors" data-id="element-86">
                  <div className="text-[#F5A623] font-bold text-sm tracking-wider uppercase mb-2" data-id="element-87">
                    {col.week}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-6 pb-4 border-b border-white/10" data-id="element-88">
                    {col.title}
                  </h3>
                  <ul className="space-y-3" data-id="element-89">
                    {col.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-gray-300 text-sm" data-id="element-90">
                        <CheckCircle2 className="w-4 h-4 text-[#2EAD6D] mt-0.5 shrink-0" data-id="element-91" />
                        <span data-id="element-92">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </div>,
  // SLIDE 8: PROVEN SYSTEM
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6" data-id="element-93">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 text-center" data-id="element-94">
          A Proven Patient Acquisition System
        </h2>
        <p className="text-xl text-[#F5A623] mb-12 text-center" data-id="element-95">
          Why this partnership delivers results
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-96">
          {[{
        icon: Target,
        title: 'Data-Driven Targeting',
        desc: 'Every campaign is built on real performance data — not guesswork. We continuously refine audiences, creatives, and spend to maximize your cost-per-lead.'
      }, {
        icon: TrendingUp,
        title: 'Scalable & Repeatable',
        desc: 'This system has been tested across multiple markets. Once dialed in, your campaigns scale predictably — more spend, more patients, same efficiency.'
      }, {
        icon: ShieldCheck,
        title: 'Full-Stack Infrastructure',
        desc: 'Ads, website, booking, tracking, and reporting — all built and managed under one roof. No gaps, no finger-pointing, no wasted budget.'
      }, {
        icon: Users,
        title: 'Dedicated Partnership',
        desc: "You're not a ticket number .Monthly strategy calls 24-hr support turnaround and a team that treats your growth like their own ."
      }].map((item, idx) => <div key={idx} className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 relative overflow-hidden group" data-id="element-97">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#2EAD6D] to-[#F5A623] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-98"></div>
              <div className="flex items-center gap-4 mb-4" data-id="element-99">
                <div className="bg-[#2EAD6D]/10 p-3 rounded-xl" data-id="element-100">
                  <item.icon className="w-7 h-7 text-[#2EAD6D]" data-id="element-101" />
                </div>
                <h3 className="text-xl font-bold text-white" data-id="element-102">{item.title}</h3>
              </div>
              <p className="text-gray-400 leading-relaxed" data-id="element-103">{item.desc}</p>
            </div>)}
        </div>

        <div className="bg-gradient-to-r from-[#2EAD6D]/15 to-[#F5A623]/15 border border-[#2EAD6D]/20 rounded-2xl p-8 text-center" data-id="element-104">
          <p className="text-2xl font-bold text-white mb-2" data-id="element-105">
            "We don't just run ads — we build acquisition engines."
          </p>
          <p className="text-gray-400" data-id="element-106">— Viridia Analytics</p>
        </div>
      </div>,
  // SLIDE 9: REVIEWS
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6" data-id="element-107">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-108">
          Performance Reviews
        </h2>
        <p className="text-xl text-[#F5A623] mb-12" data-id="element-109">Monthly Review Cadence</p>

        <div className="space-y-6" data-id="element-110">
          {[{
        time: '30-Day',
        title: 'Check-In Call',
        desc: ['Review early campaign data & lead quality', 'Adjust targeting and ad spend as needed'],
        color: 'border-[#2EAD6D]'
      }, {
        time: '60-Day',
        title: 'Mid-Point Review',
        desc: ['Analyze conversion rates and cost-per-lead', 'Optimize underperforming channels'],
        color: 'border-[#F5A623]'
      }, {
        time: '90-Day',
        title: 'Full Performance Audit',
        desc: ['Compare results to original KPIs', 'Present ROI report and next-quarter roadmap'],
        color: 'border-blue-500'
      }].map((item, idx) => <div key={idx} className={`bg-[#1A1A1A] p-8 rounded-2xl border-l-4 ${item.color} flex flex-col md:flex-row gap-6 items-start md:items-center`} data-id="element-111">
              <div className="md:w-1/3" data-id="element-112">
                <div className="text-3xl font-bold text-white" data-id="element-113">{item.time}</div>
                <div className="text-gray-400 font-medium" data-id="element-114">{item.title}</div>
              </div>
              <div className="md:w-2/3 space-y-2" data-id="element-115">
                {item.desc.map((d, i) => <div key={i} className="flex items-center gap-3 text-gray-200" data-id="element-116">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-117"></div>
                    {d}
                  </div>)}
              </div>
            </div>)}
        </div>
      </div>,
  // SLIDE 9: SUCCESS REQUIREMENTS
  () => <div className="flex flex-col justify-center h-full max-w-6xl mx-auto px-6" data-id="element-118">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-119">
          What Drives Success
        </h2>
        <p className="text-xl text-[#F5A623] mb-10" data-id="element-120">
          Success Requirements (Client Responsibilities)
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-121">
          {[{
        title: 'Account Access',
        desc: 'Provide ad account, domain, and asset access immediately.'
      }, {
        title: 'Funding',
        desc: 'Ad spend budget must be funded directly on platforms.'
      }, {
        title: 'Feedback Loop',
        desc: 'Approve creatives and strategy within 48 hours to avoid delays.'
      }, {
        title: 'Lead Management',
        desc: 'The partner is responsible for sales, consultations, and onboarding.'
      }, {
        title: 'Compliance',
        desc: 'Ensure all offers and claims meet medical & legal regulations.'
      }, {
        title: 'Performance Data',
        desc: 'Shared feedback on lead quality is vital for optimization.'
      }].map((item, idx) => <div key={idx} className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 flex flex-col h-full" data-id="element-122">
              <div className="flex items-center gap-3 mb-4" data-id="element-123">
                <CheckCircle2 className="w-6 h-6 text-[#2EAD6D]" data-id="element-124" />
                <h3 className="text-xl font-bold text-white" data-id="element-125">{item.title}</h3>
              </div>
              <p className="text-gray-400 leading-relaxed flex-grow" data-id="element-126">
                {item.desc}
              </p>
            </div>)}
        </div>
      </div>,
  // SLIDE 11: WEBSITES
  () => <div className="flex flex-col justify-center h-full max-w-6xl mx-auto px-6" data-id="element-127">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-128">
          Website Hosting & Templates
        </h2>

        <div className="bg-[#1A1A1A] p-6 rounded-2xl border border-white/10 mb-8" data-id="element-129">
          <p className="text-gray-200 text-lg mb-2" data-id="element-130">
            Our Web hosting Provider is GoDaddy. We will Host your domain,
            Create your website, and develop your brand identity with the tools
            provided.
          </p>
          <p className="text-[#F5A623] text-sm" data-id="element-131">
            This service only provides securing your .com (excluding .net .co
            .io etc.)
          </p>
        </div>

        <h3 className="text-2xl font-bold text-white mb-6" data-id="element-132">
          Interactive Website Template Demo
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-133">
          {[{
        title: 'Performance Focus',
        url: 'https://levate1.viridiaanalytics.com',
        desc: 'Bold, high-energy design optimized for conversions'
      }, {
        title: 'Clinical / Evidence Focus',
        url: 'https://levate2.viridiaanalytics.com',
        desc: 'Clean, trust-building layout with clinical authority'
      }, {
        title: 'Lifestyle / Transformation Focus',
        url: 'https://levate3.viridiaanalytics.com',
        desc: 'Warm, aspirational design focused on transformation'
      }].map((template, idx) => <a key={idx} href={template.url} target="_blank" rel="noopener noreferrer" className="group relative rounded-xl overflow-hidden border border-white/10 bg-black hover:border-[#2EAD6D] transition-all block" data-id="element-134">
              <div className="aspect-[4/3] relative overflow-hidden bg-gray-900" data-id="element-135">
                <iframe src={template.url} title={template.title} className="absolute top-0 left-0 w-[300%] h-[300%] border-0 pointer-events-none" style={{
            transform: 'scale(0.333)',
            transformOrigin: 'top left'
          }} loading="lazy" data-id="element-136" />
                {/* Clickable overlay */}
                <div className="absolute inset-0 z-10 bg-transparent group-hover:bg-black/20 transition-colors" data-id="element-137"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent z-20" data-id="element-138"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 z-30" data-id="element-139">
                <span className="font-bold text-white text-base block mb-1" data-id="element-140">
                  {template.title}
                </span>
                <p className="text-xs text-gray-400 mb-3" data-id="element-141">{template.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-xs text-[#2EAD6D] group-hover:text-[#34C47C] font-medium transition-colors" data-id="element-142">
                  Open Full Preview <ExternalLink className="w-3 h-3" data-id="element-143" />
                </span>
              </div>
            </a>)}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-xs text-gray-500 bg-black/40 p-4 rounded-xl" data-id="element-144">
          <p data-id="element-145">
            * All sites will include "Powered by: Viridia Analytics" in footer
          </p>
          <p data-id="element-146">* Your Brand Color Palette, Logo, and Stock photos will be used</p>
          <p data-id="element-147">* Additional Pages, widgets, funnels subject to additional fees</p>
          <p data-id="element-148">
            * Existing domains require access for tracking (Meta Pixel, UTM)
          </p>
        </div>
      </div>,
  // SLIDE 12: BRANDING
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6 text-center" data-id="element-149">
        <h2 className="text-5xl md:text-6xl font-bold text-white mb-4" data-id="element-150">
          Build a trusted <span className="text-[#2EAD6D]" data-id="element-151">BRAND</span>
        </h2>
        <p className="text-gray-400 mb-12 text-lg" data-id="element-152">
          Here's an example of what we'll create for you
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-153">
          {/* Example Logo */}
          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 flex flex-col items-center" data-id="element-154">
            <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#1B4D6E] flex items-center justify-center mb-6 shadow-lg" data-id="element-155">
              <div className="text-center" data-id="element-156">
                <Shield className="w-10 h-10 text-[#2EAD6D] mx-auto mb-1" data-id="element-157" />
                <span className="text-white font-bold text-xs tracking-widest" data-id="element-158">
                  APEX
                </span>
              </div>
            </div>
            <h4 className="text-lg font-bold text-white mb-1" data-id="element-159">Logo</h4>
            <p className="text-sm text-gray-500" data-id="element-160">Custom mark + wordmark</p>
          </div>

          {/* Example Color Palette */}
          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 flex flex-col items-center" data-id="element-161">
            <div className="flex gap-2 mb-6" data-id="element-162">
              {[{
            color: '#0A2540',
            label: 'Navy'
          }, {
            color: '#2EAD6D',
            label: 'Teal'
          }, {
            color: '#1B4D6E',
            label: 'Steel'
          }, {
            color: '#F5F5F0',
            label: 'Cream'
          }, {
            color: '#D4A853',
            label: 'Gold'
          }].map((swatch, i) => <div key={i} className="flex flex-col items-center gap-2" data-id="element-163">
                  <div className="w-12 h-12 rounded-lg shadow-md border border-white/10" style={{
              backgroundColor: swatch.color
            }} data-id="element-164" />
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-165">
                    {swatch.label}
                  </span>
                </div>)}
            </div>
            <h4 className="text-lg font-bold text-white mb-1" data-id="element-166">Color Palette</h4>
            <p className="text-sm text-gray-500" data-id="element-167">Curated brand colors</p>
          </div>

          {/* Example Brand Motto */}
          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 flex flex-col items-center justify-center" data-id="element-168">
            <MessageSquareQuote className="w-8 h-8 text-[#F5A623] mb-4" data-id="element-169" />
            <p className="text-2xl font-bold text-white italic leading-snug mb-4" data-id="element-170">
              "Strength Restored.
              <br data-id="element-171" />
              Confidence Renewed."
            </p>
            <h4 className="text-lg font-bold text-white mb-1" data-id="element-172">Brand Motto</h4>
            <p className="text-sm text-gray-500" data-id="element-173">Memorable tagline</p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#2EAD6D]/10 via-transparent to-[#F5A623]/10 p-10 rounded-3xl border border-white/5" data-id="element-174">
          <h3 className="text-3xl font-bold text-white mb-6" data-id="element-175">
            Build a brand patients remember, not just a logo.
          </h3>
          <p className="text-xl text-gray-400" data-id="element-176">
            Already have branding? Perfect.
            <br data-id="element-177" />
            We'll plug it into high-performing ad creatives that actually get
            clicks.
          </p>
        </div>
      </div>,
  // SLIDE 13: ADD-ONS (MOVED HERE)
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6" data-id="element-178">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-179">
          A La Carte Add-Ons
        </h2>

        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#1A1A1A]" data-id="element-180">
          <table className="w-full text-left border-collapse" data-id="element-181">
            <thead data-id="element-182">
              <tr className="border-b border-white/10 bg-black/40" data-id="element-183">
                <th className="p-6 text-gray-400 font-medium" data-id="element-184">Service</th>
                <th className="p-6 text-gray-400 font-medium" data-id="element-185">Regular Price</th>
                <th className="p-6 text-[#F5A623] font-bold bg-[#F5A623]/5" data-id="element-186">
                  14-Day Price (10% Off)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5" data-id="element-187">
              {[['GHL Phone Number', '$10/mo', '$9/mo'], ['Email Marketing Campaigns', '$197 per campaign', '$177.30 per campaign'], ['Social Media Automation', '$247/mo', '$222.30/mo'], ['SMS Broadcast Messaging', '$97/mo', '$87.30/mo'], ['Advanced Pipeline Customization', '$497 one-time', '$447.30 one-time'], ['AI Voice Agent', '$297/mo', '$267.30/mo']].map((row, idx) => <tr key={idx} className="hover:bg-white/5 transition-colors" data-id="element-188">
                  <td className="p-6 text-gray-200 font-medium" data-id="element-189">{row[0]}</td>
                  <td className="p-6 text-gray-400" data-id="element-190">{row[1]}</td>
                  <td className="p-6 text-[#2EAD6D] font-bold bg-[#F5A623]/5" data-id="element-191">
                    {row[2]}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>,
  // SLIDE 14: LEGAL
  () => <div className="flex flex-col justify-center h-full max-w-4xl mx-auto px-6" data-id="element-192">
        <h2 className="text-3xl font-bold text-gray-300 mb-8 border-b border-white/10 pb-4" data-id="element-193">
          Legal Disclaimer & Important Notices
        </h2>

        <div className="space-y-6 text-sm md:text-base text-gray-500 leading-relaxed" data-id="element-194">
          <p data-id="element-195">
            This presentation is for informational purposes only and does not
            constitute a legal, financial, or earnings guarantee of any kind.
          </p>
          <p data-id="element-196">
            Any revenue, profit, or growth examples are illustrative only;
            actual results vary based on each licensee's skills, effort, market
            conditions, and business decisions.
          </p>
          <p data-id="element-197">
            Past performance of Viridia Analytics, its partners, or other
            licensees does not guarantee future results for you or your
            business.
          </p>
          <p data-id="element-198">
            By participating in this program, you acknowledge that you are
            operating as an independent contractor and not as an employee,
            partner, or agent of Viridia Analytics.
          </p>
          <p data-id="element-199">
            You are solely responsible for complying with all applicable laws,
            regulations, advertising guidelines, and tax obligations in your
            jurisdiction.
          </p>
          <p data-id="element-200">
            Nothing in this presentation should be considered legal, tax, or
            professional advice; consult your own qualified advisors before
            making decisions.
          </p>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 text-xs text-gray-600" data-id="element-201">
          © 2026 Viridia Analytics. All rights reserved. Confidential and
          proprietary – do not distribute without written permission.
        </div>
      </div>,
  // SLIDE 15: CONTACT INFO
  () => <div className="flex flex-col justify-center h-full max-w-4xl mx-auto px-6" data-id="element-202">
        <div className="text-center mb-12" data-id="element-203">
          <img src={LOGO_VIRIDIA} alt="Viridia Analytics" className="h-24 md:h-32 object-contain mx-auto mb-8" data-id="element-204" />
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-205">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-400" data-id="element-206">
            We're here to support your success
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-207">
          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5" data-id="element-208">
            <div className="flex items-center gap-4 mb-4" data-id="element-209">
              <div className="bg-[#2EAD6D]/10 p-3 rounded-xl" data-id="element-210">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#2EAD6D]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-211">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" data-id="element-212" />
                </svg>
              </div>
              <div data-id="element-213">
                <h3 className="text-lg font-bold text-white" data-id="element-214">Phone</h3>
                <p className="text-[#2EAD6D] text-lg font-medium" data-id="element-215">
                  (623) 300-0708
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5" data-id="element-216">
            <div className="flex items-center gap-4 mb-4" data-id="element-217">
              <div className="bg-[#F5A623]/10 p-3 rounded-xl" data-id="element-218">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#F5A623]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-219">
                  <circle cx="12" cy="12" r="10" data-id="element-220" />
                  <polyline points="12 6 12 12 16 14" data-id="element-221" />
                </svg>
              </div>
              <div data-id="element-222">
                <h3 className="text-lg font-bold text-white" data-id="element-223">Business Hours</h3>
                <p className="text-gray-300" data-id="element-224">Mon – Friday</p>
                <p className="text-[#F5A623] font-medium" data-id="element-225">
                  9:00 AM – 4:00 PM (AZ Time)
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5" data-id="element-226">
            <div className="flex items-center gap-4 mb-4" data-id="element-227">
              <div className="bg-[#2EAD6D]/10 p-3 rounded-xl" data-id="element-228">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#2EAD6D]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-229">
                  <rect width="20" height="16" x="2" y="4" rx="2" data-id="element-230" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" data-id="element-231" />
                </svg>
              </div>
              <div data-id="element-232">
                <h3 className="text-lg font-bold text-white" data-id="element-233">
                  General Inquiries
                </h3>
                <a href="mailto:info@viridiaanalytics.com" className="text-[#2EAD6D] hover:text-[#34C47C] font-medium transition-colors" data-id="element-234">
                  info@viridiaanalytics.com
                </a>
              </div>
            </div>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5" data-id="element-235">
            <div className="flex items-center gap-4 mb-4" data-id="element-236">
              <div className="bg-[#F5A623]/10 p-3 rounded-xl" data-id="element-237">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#F5A623]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-238">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4-4v2" data-id="element-239" />
                  <circle cx="9" cy="7" r="4" data-id="element-240" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" data-id="element-241" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" data-id="element-242" />
                </svg>
              </div>
              <div data-id="element-243">
                <h3 className="text-lg font-bold text-white" data-id="element-244">
                  Point of Contact (Support)
                </h3>
                <a href="mailto:kevin@viridiaanalytics.com" className="text-[#F5A623] hover:text-[#F7B84E] font-medium transition-colors" data-id="element-245">
                  kevin@viridiaanalytics.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>,
  // SLIDE 15: CLOSING
  () => <div className="flex flex-col items-center justify-center h-full text-center px-4" data-id="element-246">
        <div className="flex items-center justify-center gap-6 mb-12" data-id="element-247">
          <img src={LOGO_LEVATE} alt="L-Evate" className="h-12 md:h-16 object-contain opacity-80" data-id="element-248" />
          <span className="text-2xl font-light text-gray-600" data-id="element-249">x</span>
          <img src={LOGO_VIRIDIA} alt="Viridia Analytics" className="h-28 md:h-36 object-contain" data-id="element-250" />
        </div>

        <h2 className="text-5xl md:text-7xl font-bold text-white mb-6" data-id="element-251">
          Questions?
        </h2>
        <p className="text-2xl text-[#2EAD6D] font-medium mb-6" data-id="element-252">
          Let's build something powerful together.
        </p>
        <div className="w-24 h-1 bg-gradient-to-r from-[#F5A623] to-[#2EAD6D] rounded-full" data-id="element-253"></div>
      </div>];
  return <div className="min-h-screen bg-[#0F0F0F] text-white overflow-hidden flex flex-col font-sans selection:bg-[#2EAD6D] selection:text-white" data-id="element-254">
      {/* Progress Bar */}
      <div className="h-1.5 w-full bg-white/5 fixed top-0 left-0 z-50" data-id="element-255">
        <div className="h-full bg-gradient-to-r from-[#F5A623] to-[#2EAD6D] transition-all duration-500 ease-out" style={{
        width: `${(currentSlide + 1) / totalSlides * 100}%`
      }} data-id="element-256" />
      </div>

      {/* Main Slide Content */}
      <main className="flex-grow relative" data-id="element-257">
        <div key={currentSlide} className="absolute inset-0 animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both" data-id="element-258">
          {slides[currentSlide]()}
        </div>
      </main>

      {/* Navigation Controls */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-[#1A1A1A]/80 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 z-50" data-id="element-259">
        <button onClick={prevSlide} disabled={currentSlide === 0} className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors" aria-label="Previous slide" data-id="element-260">
          <ChevronLeft className="w-6 h-6" data-id="element-261" />
        </button>

        <div className="text-sm font-medium text-gray-300 min-w-[4rem] text-center" data-id="element-262">
          {currentSlide + 1} / {totalSlides}
        </div>

        <button onClick={nextSlide} disabled={currentSlide === totalSlides - 1} className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors" aria-label="Next slide" data-id="element-263">
          <ChevronRight className="w-6 h-6" data-id="element-264" />
        </button>

        <div className="w-px h-6 bg-white/10" data-id="element-265"></div>

        <button onClick={openPresenterView} className="p-2 text-gray-400 hover:text-[#F5A623] transition-colors" aria-label="Open presenter view" title="Speaker Notes" data-id="element-266">
          <Presentation className="w-5 h-5" data-id="element-267" />
        </button>

        <button onClick={toggleFullscreen} className="p-2 text-gray-400 hover:text-white transition-colors" aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} data-id="element-268">
          {isFullscreen ? <Minimize2 className="w-5 h-5" data-id="element-269" /> : <Maximize2 className="w-5 h-5" data-id="element-270" />}
        </button>
      </div>
    </div>;
}